import { useNavigate } from '@tanstack/react-router'

import { OrderListItem } from '@/api/admin-orders-types'
import { Button } from '@/components/base/button'
import { StatusBadge } from '@/components/base/status-badge'
import { StatusColor } from '@/components/base/status-cell'
import { Text } from '@/components/base/text'
import { Thumbnail } from '@/components/base/thumbnail'
import { useI18n } from '@/i18n/i18n-react'

interface OrderDetailProps {
	order: OrderListItem
}

const getPaymentStatusColor = (status: string): StatusColor => {
	switch (status) {
		case 'PAID':
			return 'green'
		case 'PENDING':
		case 'PARTIALLY_PAID':
		case 'PARTIALLY_REFUNDED':
			return 'orange'
		case 'REFUNDED':
			return 'red'
		default:
			return 'grey'
	}
}

const DETAIL_BASE = 150
const ITEM_ROW = 37
const MAX_TABLE_ROWS = 6

export function getOrderDetailHeight(order: OrderListItem): number {
	const itemCount = Math.min(order.items?.length ?? 0, MAX_TABLE_ROWS)
	const txCount = Math.min(order.transactions?.length ?? 0, MAX_TABLE_ROWS)
	const tableRows = Math.max(itemCount, txCount, 1)
	return DETAIL_BASE + tableRows * ITEM_ROW
}

export function OrderDetail({ order }: OrderDetailProps) {
	const { L } = useI18n()
	const navigate = useNavigate()
	const items = order.items ?? []
	const transactions = order.transactions ?? []

	return (
		<div className='flex flex-col gap-4'>
			{/* Status Row */}
			<div className='flex items-center gap-4'>
				<div className='flex items-center gap-2'>
					<Text size='xsmall' className='text-ui-fg-muted'>
						{L.Orders.Column.PaymentStatus()}
					</Text>
					<StatusBadge color={getPaymentStatusColor(order.paymentStatus)}>
						{L.Enum.PaymentStatus(order.paymentStatus)}
					</StatusBadge>
				</div>
				<div className='flex items-center gap-2'>
					<Text size='xsmall' className='text-ui-fg-muted'>
						{L.Orders.Column.FulfillmentStatus()}
					</Text>
					<StatusBadge>{L.Enum.FulfillmentStatus(order.fulfillmentStatus)}</StatusBadge>
				</div>
				{order.pointOfSale && (
					<div className='flex items-center gap-2'>
						<Text size='xsmall' className='text-ui-fg-muted'>
							{L.Fields.PointOfSale()}
						</Text>
						<Text size='small'>{order.pointOfSale}</Text>
					</div>
				)}
				<div className='ml-auto flex items-center gap-2'>
					<Text size='xsmall' className='text-ui-fg-muted'>
						{L.Orders.Column.Total()}
					</Text>
					<Text size='small' weight='plus'>
						{L.Amount.Default({
							amount: order.total,
							currencyCode: order.currencyCode
						})}
					</Text>
				</div>
			</div>

			{/* Items & Transactions Tables */}
			<div className='grid gap-4 md:grid-cols-2'>
				{/* Items Table */}
				<div className='overflow-hidden rounded-md border border-ui-border-base'>
					<table className='w-full'>
						<thead>
							<tr className='bg-ui-bg-base'>
								<th className='txt-compact-xsmall-plus border-b border-ui-border-base px-3 py-2 text-left text-ui-fg-subtle'>
									{L.OrderDetail.Items()} ({items.length})
								</th>
							</tr>
						</thead>
						<tbody className='bg-ui-bg-base'>
							{items.length === 0 ? (
								<tr>
									<td className='txt-compact-small px-3 py-2 text-ui-fg-muted'>{L.General.None()}</td>
								</tr>
							) : (
								items.map((item, index) => (
									<tr
										key={`${item.name}-${index}`}
										className='border-b border-ui-border-base last:border-b-0'
									>
										<td className='px-3 py-1.5'>
											<div className='flex items-center gap-2'>
												<Thumbnail src={item.imageUrl} size='small' />
												<span className='txt-compact-small truncate'>{item.name}</span>
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>

				{/* Transactions Table */}
				<div className='overflow-hidden rounded-md border border-ui-border-base'>
					<table className='w-full'>
						<thead>
							<tr className='bg-ui-bg-base'>
								<th className='txt-compact-xsmall-plus border-b border-ui-border-base px-3 py-2 text-left text-ui-fg-subtle'>
									{L.OrderDetail.Transactions()} ({transactions.length})
								</th>
								<th className='txt-compact-xsmall-plus border-b border-ui-border-base px-3 py-2 text-right text-ui-fg-subtle'>
									{L.Orders.Column.Total()}
								</th>
							</tr>
						</thead>
						<tbody className='bg-ui-bg-base'>
							{transactions.length === 0 ? (
								<tr>
									<td colSpan={2} className='txt-compact-small px-3 py-2 text-ui-fg-muted'>
										{L.General.None()}
									</td>
								</tr>
							) : (
								transactions.map((transaction, index) => (
									<tr
										key={`${transaction.paymentMethod}-${index}`}
										className='border-b border-ui-border-base last:border-b-0'
									>
										<td className='txt-compact-small px-3 py-1.5'>{transaction.paymentMethod}</td>
										<td className='txt-compact-small px-3 py-1.5 text-right font-medium'>
											{L.Amount.Default({
												amount: transaction.total,
												currencyCode: order.currencyCode
											})}
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</div>

			{/* Actions */}
			<div className='flex items-center gap-2'>
				<Button
					className='flex-1'
					variant='primary'
					size='small'
					onClick={event => {
						event.stopPropagation()
						navigate({
							to: '/orders/$orderUuid',
							params: { orderUuid: order.uuid }
						})
					}}
				>
					{L.OrderDetail.OpenOrderDetails()}
				</Button>
				<Button
					variant='secondary'
					size='small'
					onClick={event => {
						event.stopPropagation()
						window.open(`/orders/${order.uuid}/receipt`, '_blank')
					}}
				>
					{L.OrderDetail.Summary.OpenReceipt()}
				</Button>
			</div>
		</div>
	)
}
